import { getIn, useFormikContext } from 'formik';
import { pluralize } from 'inflection';
import _ from 'lodash';
import React, { useState } from 'react';
import BorderedIcon from '../../components/BorderedIcon';
import Button from '../../components/Button';
import Card, { Cards, CardSeparator } from '../../components/Card';
import { Field, FieldSet } from '../../components/Form';
import IconButton from '../../components/IconButton';
import clearIcon from '../../icons/clear.svg';
import styles from './IntegrationSelectSettingItem.module.scss';
import SearchableSelectByName from './SearchableSelectByName';

export const SELECT_OPTIONS = {
  all: 'all',
  custom: 'custom',
} as const;

interface IntegrationSelectSettingItemProps {
  isSubItemContainedInOtherSubItem?: (
    subItem: { id: string; name: React.ReactNode },
    otherSubItem: { id: string; name: React.ReactNode },
  ) => boolean;
  item: {
    id: string;
    name: string;
    subItems?: { id: string; name: string }[];
  };
  itemIcon: React.ReactNode;
  itemTerm: string;
  name: string;
  renderSubItem?: (subItem: {
    id: string;
    name: React.ReactNode;
  }) => React.ReactNode;
  renderSubItemSearch?: (
    subItem: { id: string; name: React.ReactNode; disabled?: boolean },
    searchValue?: string,
  ) => React.ReactNode;
  subItemTerm?: string;
}

const IntegrationSelectSettingItem = ({
  isSubItemContainedInOtherSubItem,
  item,
  itemTerm,
  itemIcon,
  name,
  renderSubItem,
  renderSubItemSearch,
  subItemTerm,
}: IntegrationSelectSettingItemProps) => {
  const subItemsTerm = subItemTerm ? pluralize(subItemTerm) : null;
  const subItemById = _.keyBy(item.subItems || [], 'id');
  const { values, setFieldValue } = useFormikContext();
  const { select, ids } = getIn(values, name) as {
    select: string;
    ids: Set<string>;
  };

  const isSubItemDisabled = (subItem: { id: string; name: React.ReactNode }) =>
    ids.has(subItem.id) ||
    (isSubItemContainedInOtherSubItem &&
      Array.from(ids).some((id) => {
        const sub = subItemById[id];
        return sub && isSubItemContainedInOtherSubItem(subItem, sub);
      }));
  const [isAdding, setIsAdding] = useState(false);

  return (
    <Card>
      <div className={styles.header}>
        <BorderedIcon>{itemIcon}</BorderedIcon>
        <div className={styles.info}>
          <div className={styles.title}>{_.startCase(itemTerm)}</div>
          {item.name}
        </div>
        <IconButton
          className={styles.clear}
          onClick={() => setFieldValue(name, undefined)}
          aria-label="Clear"
        >
          {clearIcon}
        </IconButton>
      </div>
      {item.subItems && (
        <>
          <CardSeparator />
          <FieldSet>
            {Object.keys(SELECT_OPTIONS).map((option) => (
              <Field
                key={option}
                name={`${name}.select`}
                value={option}
                label={
                  {
                    [SELECT_OPTIONS.all]: `Entire ${itemTerm}`,
                    [SELECT_OPTIONS.custom]: `Specific ${subItemsTerm ?? ''}`,
                  }[option]
                }
                type="radio"
              />
            ))}
          </FieldSet>
          {select === SELECT_OPTIONS.custom && (
            <>
              {ids.size > 0 && (
                <div className={styles.section}>
                  <Cards>
                    {[...ids].map((id) => {
                      const sub = subItemById[id];
                      if (!sub) {
                        return null;
                      }
                      return (
                        <Card key={id}>
                          <div className={styles.header}>
                            <div className={styles.subItem}>
                              {renderSubItem?.(sub)}
                            </div>
                            <IconButton
                              className={styles.clear}
                              onClick={() =>
                                setFieldValue(
                                  `${name}.ids`,
                                  new Set(
                                    [...ids].filter(
                                      (selectedId) => selectedId !== id,
                                    ),
                                  ),
                                )
                              }
                              aria-label="Remove"
                            >
                              {clearIcon}
                            </IconButton>
                          </div>
                        </Card>
                      );
                    })}
                  </Cards>
                </div>
              )}
              <div className={styles.section}>
                {isAdding ? (
                  <SearchableSelectByName
                    items={item.subItems ?? []}
                    renderItem={(subItem, searchValue) =>
                      renderSubItemSearch?.(
                        {
                          ...subItem,
                          disabled: isSubItemDisabled(subItem),
                        },
                        searchValue,
                      )
                    }
                    placeholder={`Add ${subItemTerm ?? ''}`}
                    onSelect={(subItem) => {
                      if (!isSubItemDisabled(subItem)) {
                        setFieldValue(
                          `${name}.ids`,
                          new Set([
                            ...(isSubItemContainedInOtherSubItem
                              ? Array.from(ids).filter((id) => {
                                  const sub = subItemById[id];
                                  if (!sub) {
                                    return null;
                                  }
                                  return !isSubItemContainedInOtherSubItem(
                                    sub,
                                    subItem,
                                  );
                                })
                              : ids),
                            subItem.id,
                          ]),
                        );
                        setIsAdding(false);
                      }
                    }}
                    onClose={() => setIsAdding(false)}
                  />
                ) : (
                  <Button onClick={() => setIsAdding(true)} variant="secondary">
                    Add {subItemTerm}
                  </Button>
                )}
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
};

export default IntegrationSelectSettingItem;
